import { useState } from "react";
import type { ReactNode } from "react";
import "./Why.css";

/**
 * ADR 0009 §4 — "Prose is one line per section. Anything longer becomes a
 * Why? disclosure the reader opens."
 *
 * A native `<details>`/`<summary>`, not a custom button + conditional div:
 * the browser already gives a disclosure widget a correct role, a keyboard
 * (Enter/Space toggles, and it is natively focusable), and a persisted open
 * state — reimplementing that is exactly the kind of extra control ADR 0009
 * is retiring elsewhere on these same screens.
 *
 * CONTROLLED, not left to the browser alone. Native `<details>` never
 * reflects its open/closed state as an `aria-expanded` ATTRIBUTE — that
 * mapping exists only in the accessibility tree, not the DOM — and a caller
 * (or a test) reading `aria-expanded` off the `<summary>` would find nothing
 * there. Mirroring `open` into `aria-expanded` on every toggle makes the
 * state readable both ways without fighting the native widget: the click
 * still opens/closes it, and `onToggle` only syncs React's copy afterward.
 *
 * COLLAPSED BY DEFAULT, every time — `useState(false)`, never a prop that
 * could default it open, because the whole point is that the long text is
 * not on screen until the reader chooses it.
 *
 * `role="button"` IS EXPLICIT, not redundant. MEASURED on the pinned Chromium
 * (v151): a native `<summary>`'s own implicit role is its own internal
 * "disclosure triangle" concept, not ARIA `button` — `getByRole("button", …)`
 * finds zero matches against an unstyled, unmodified `<summary>` in this
 * browser, open or closed. The override corrects that without giving up the
 * native element underneath (Enter/Space toggling, focusability, the browser's
 * own click handling are all still the real `<summary>`'s).
 *
 * `.why:not([open]) > .why__content { display: none }` in Why.css is there for
 * the same reason and is equally load-bearing, not decoration: this Chromium
 * hides collapsed `<details>` content via `content-visibility: hidden`, which
 * still generates a layout box (`offsetParent`/`getBoundingClientRect` stay
 * non-null/non-zero) — so a test asserting on `offsetParent` to prove
 * "collapsed content is invisible" would incorrectly read it as shown. An
 * explicit author-stylesheet `display: none` always wins over a user-agent
 * rule in the cascade, so it forces the unambiguous, cross-browser signal.
 */
export function Why({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <details
      className="why"
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="why__summary" role="button" aria-expanded={open}>
        Why?
      </summary>
      <div className="why__content">{children}</div>
    </details>
  );
}
